import React, {useContext, useEffect} from "react";
import {Box, Card, Stack, Typography} from "@mui/material";
import {
  CONTEXT_ACTION_TYPE,
  CUSTOM_WORLD_REGISTER_STEPS,
  CustomWorldRegisterPayload,
  DataStoreContext,
  initialState
} from "../../contexts/OperatingEnvironmentContext";
import useMutation from "../../../../hooks/useMutation";
import Pjdb1ImputEx from "../../../../components/inputs/Pjdb1ImputEx";
import WithLabel from "../forms/WithLabel";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {BaseWorldSelectionField} from "../forms/BaseWorldSelectionField";
import {useBaseWorld} from "../../hooks/useBaseWorld";
import {WorkModelSelectionField} from "../forms/WorkModelSelectionField";
import {useWorkClass} from "../../hooks/useWorlClass";
import {useWorkInstanceByClass} from "../../hooks/useWorkInstance";
import {useWorkModelByInstance} from "../../hooks/useWorkModel";
import {buildGazeboUiUri} from "../../../../utils/gazeboUtils";

export default function CustomWorldRegisterSelectionStep() {
  const {state, dispatch} = useContext(DataStoreContext);
  const {fetchAll: fetchBaseWorlds} = useBaseWorld({
    searchCond: state.customWorldRegister.baseWorld.searchCond,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: {
          baseWorld: {
            ...state.customWorldRegister.baseWorld,
            items: newData,
            searchStatus: newStatus
          },
        } satisfies CustomWorldRegisterPayload
      });
    },
  });
  const {mutate, formRef, isLoading, clear} = useMutation({
    enableConfirm: false,
    mutationFn: async () => {
      // 事前にバリデーション済み
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      window.open(buildGazeboUiUri(state.customWorldRegister.inputs.baseWorld!.url.glb), '_blank');
      return true;
    },
    validate: () => {
      if (state.customWorldRegister.inputs.baseWorld === null) {
        return "ベースワールドを選択してください。";
      }
      if (state.customWorldRegister.inputs.workModels.length === 0) {
        return "対象物を選択してください。";
      }
      return true;
    },
    onClear: () => {
      console.log("onClear");
      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: initialState.customWorldRegister satisfies CustomWorldRegisterPayload
      });
    },
    onCompleted: () => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: {
          step: CUSTOM_WORLD_REGISTER_STEPS.WORLD_INFO_INPUT_STEP,
        } satisfies CustomWorldRegisterPayload
      });
    },
  });

  return (
    <Card sx={{p: 3, width: '100%'}}>
      <Stack gap={4}>
        <Box display='flex' flexWrap='wrap' gap={2} alignItems='flex-end' justifyContent='space-between'>
          <Typography variant="h6">カスタムワールド</Typography>
          <Pjdb1ImputEx/>
        </Box>

        <Box>
          <Typography variant="body2">
            ベースワールドと対象物を選択後、ベースワールドを確認を押してください。<br/>
            設定ファイルの作成時、ベースワールドを参照します。
          </Typography>
        </Box>

        <form ref={formRef}>
          <Stack gap={2}>
            <WithLabel label='ベースワールド'>
              <Box sx={{width: '100%', mt: 2}}/>
              <BaseWorldSelectionField
                baseWorlds={state.customWorldRegister.baseWorld.items.map(item => Object.values(item)).flat()}
                fetchBaseWorlds={fetchBaseWorlds}
                searchStatus={state.customWorldRegister.baseWorld.searchStatus}
                searchCond={state.customWorldRegister.baseWorld.searchCond}
                selectedBaseWorld={state.customWorldRegister.inputs.baseWorld}
                onChange={(baseWorlds) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
                    payload: {
                      inputs: {
                        ...state.customWorldRegister.inputs,
                        baseWorld: baseWorlds
                      }
                    } satisfies CustomWorldRegisterPayload
                  });
                }}
                onChangeSearchCond={(searchCond) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
                    payload: {
                      baseWorld: {
                        ...state.customWorldRegister.baseWorld,
                        searchCond
                      }
                    } satisfies CustomWorldRegisterPayload
                  });
                }}
              />
            </WithLabel>
            <WithLabel label='対象物'>
              <Box mt={2}>
                <WorkSelectionField/>
              </Box>
            </WithLabel>
          </Stack>
        </form>

        <Box component="section" sx={{textAlign: "center"}}>
          <Pjdb1Button
            label="クリア"
            variant="contained"
            color="inherit"
            isLoading={isLoading}
            sx={{m: 2}}
            onClick={clear}
          />
          <Pjdb1Button
            label="ベースワールドを確認"
            variant="contained"
            color="primary"
            isLoading={isLoading}
            disabled={state.customWorldRegister.inputs.baseWorld === null}
            sx={{m: 2}}
            onClick={() => {
              mutate().catch(console.error)
            }}
          />
        </Box>
      </Stack>
    </Card>
  );
}

function WorkSelectionField() {
  const {state, dispatch} = useContext(DataStoreContext);
  const work = useWork();

  useEffect(() => {
    console.log('onFetchInstance');
    work.workInstanceResult.fetchAll().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.customWorldRegister.workClass.selected]);

  useEffect(() => {
    console.log('onFetchModel');
    work.workModelResult.fetchAll().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.customWorldRegister.workInstance.selected]);

  return (
    <WorkModelSelectionField
      step={state.customWorldRegister.workModelSelectionStep}
      onChangeStep={(step) => {
        dispatch({
          type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
          payload: {
            workModelSelectionStep: step
          } satisfies CustomWorldRegisterPayload
        });
      }}
      // Work Class
      searchCond={state.customWorldRegister.workClass.searchCond}
      onChangeSearchCond={(searchCond) => {
        dispatch({
          type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
          payload: {
            workClass: {
              ...state.customWorldRegister.workClass,
              searchCond
            }
          } satisfies CustomWorldRegisterPayload
        });
      }}
      fetchWorkClasses={work.workClassResult.fetchAll}
      searchWorkClassStatus={state.customWorldRegister.workClass.searchStatus}
      workClasses={
        state.customWorldRegister.workClass.items
          .map(item => Object.values(item.items))
          .flat()
      }
      selectedWorkClass={state.customWorldRegister.workClass.selected}
      // Work Instance
      onChangeClass={(workClass) => {
        dispatch({
          type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
          payload: {
            workClass: {
              ...state.customWorldRegister.workClass,
              selected: workClass,
            },
          } satisfies CustomWorldRegisterPayload
        });
      }}
      workInstances={
        state.customWorldRegister.workInstance.items
          .map(item => Object.values(item.items))
          .flat()
      }
      searchWorkInstanceStatus={state.customWorldRegister.workInstance.searchStatus}
      selectedWorkInstance={state.customWorldRegister.workInstance.selected}
      onChangeInstance={(workInstance) => {
        dispatch({
          type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
          payload: {
            workInstance: {
              ...state.customWorldRegister.workInstance,
              selected: workInstance,
            },
          } satisfies CustomWorldRegisterPayload
        });
      }}
      // Work Model
      searchWorkModelStatus={state.customWorldRegister.workModel.searchStatus}
      workModels={
        state.customWorldRegister.workModel.items
          .map(item => Object.values(item.items))
          .flat()
      }
      selectedWorkModels={state.customWorldRegister.inputs.workModels}
      onChangeModels={(workModels) => {
        dispatch({
          type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
          payload: {
            inputs: {
              ...state.customWorldRegister.inputs,
              workModels
            }
          } satisfies CustomWorldRegisterPayload
        });
      }}
    />
  )
}

function useWork() {
  const {state, dispatch} = useContext(DataStoreContext);

  const workClassResult = useWorkClass({
    searchCond: state.customWorldRegister.workClass.searchCond,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: {
          workClass: {
            ...state.customWorldRegister.workClass,
            items: newData,
            searchStatus: newStatus
          },
        } satisfies CustomWorldRegisterPayload
      });
    },
  });

  const workInstanceResult = useWorkInstanceByClass({
    workClassIds: state.customWorldRegister.workClass.selected?.attr['class-id']
      ? [state.customWorldRegister.workClass.selected?.attr['class-id']]
      : [],
    onChange: (newData, newStatus) => {
      const uniqueIds = Array.from(
        new Set(
          newData
            .flatMap((item) => Object.values(item.items))
            .map((item) => item.attr["instance-id"])
        )
      );
      const isUnique = uniqueIds.length === newData.flatMap((item) => Object.values(item.items)).length;
      // タブ切り替えでデータが重複するため
      const uniqueItems = isUnique ? newData : newData.slice(0, newData.length / 2);

      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: {
          workInstance: {
            ...state.customWorldRegister.workInstance,
            items: uniqueItems,
            searchStatus: newStatus
          },
        } satisfies CustomWorldRegisterPayload
      });
    },
  });

  const workModelResult = useWorkModelByInstance({
    workInstanceIds: state.customWorldRegister.workInstance.selected?.attr['instance-id']
      ? [state.customWorldRegister.workInstance.selected?.attr['instance-id']]
      : [],
    onChange: (newData, newStatus) => {
      const uniqueIds = Array.from(
        new Set(
          newData
            .flatMap((item) => Object.values(item.items))
            .map((item) => item.attr["model-id"])
        )
      );
      const isUnique = uniqueIds.length === newData.flatMap((item) => Object.values(item.items)).length;
      // タブ切り替えでデータが重複するため
      const uniqueItems = isUnique ? newData : newData.slice(0, newData.length / 2);

      dispatch({
        type: CONTEXT_ACTION_TYPE.CUSTOM_WORLD_REGISTER,
        payload: {
          workModel: {
            ...state.customWorldRegister.workModel,
            items: uniqueItems,
            searchStatus: newStatus
          },
        } satisfies CustomWorldRegisterPayload
      });
    },
  });

  return {
    workClassResult,
    workInstanceResult,
    workModelResult,
  };
}
